<template lang="pug">
  div
    div(class="billing-plans")
      template(v-if="loading")
        v-skeleton-loader(
          v-for="loader in 3"
          type="card, actions, text@5"
          height="550"
        )
      template(v-else)
        div(
          v-for="plan in plans"
          :key="plan.id"
          class="billing-plans__item"
          :class="{ 'gradient-bg': plan.id === premiumPlanId }"
        )
          div(class="billing-plans__item-header")
            billing-premium-label(v-if="plan.id === premiumPlanId")
            div(
              v-else
              class="text-16px dark"
            ) {{ _.upperFirst(plan.name) }}
            div(
              v-if="plan.id === currentPlan?.tariff.id"
              class="billing-plans__selected-label text-12px"
            ) {{ $t('Selected plan') }}
          div(class="billing-plans__item-content")
            h3(class="title-24px fw-500") {{ plan.subscriptionPrice / 100 }}
              |
              |
              span(class="text-14px fw-300 dark") {{ $t('₴/month') }}
            div(
              class="text-12px fw-300 pt-2"
              :class="{ 'color-primary': plan.id === premiumPlanId }"
            ) {{ plan.id === premiumPlanId && $Organization.canActivateTrial ? $t('First 14 days for free!') : plan.title }}
            v-btn(
              v-if="plan.id === premiumPlanId || (plan.id === freePlanId && currentPlan?.tariff.id === freePlanId)"
              class="w-100 mt-6"
              height="48"
              :class="{ 'main-button': plan.id === premiumPlanId }"
              :disabled="checkDisabledPlan(plan.id)"
              :loading="plan.id === premiumPlanId ? updateToPremiumLoading : false"
              @click="updateToPremium(false)"
            ) {{ setButtonText(plan.id) }}
            div(
              v-if="plan.id === premiumPlanId || (plan.id === freePlanId && currentPlan?.tariff.id === freePlanId)"
              class="text-12px fw-300 text-center py-2"
            ) {{ setPlanSubtext(plan.id) }}
            div(class="billing-plans__features")
              div(
                v-for="feature in getPlanFeatures(plan.description)"
                :key="feature"
                class="billing-plans__feature fw-300 dark"
                :class="[xlDevice ? 'text-14px' : 'text-12px']"
              )
                e-svg-icon check-black
                span {{ feature }}
        div(class="billing-plans__item")
          div(class="billing-plans__item-header")
            div(class="text-16px dark") Premium+
            //div(
            //  v-if="plan.id === currentPlan.tariff.id"
            //  class="billing-plans__selected-label text-12px"
            //) {{ $t('Selected plan') }}
          div(class="billing-plans__item-content")
            h3(class="title-24px fw-500") {{ $t('Individually') }}
            div(class="text-12px fw-300 pt-2") {{ $t('A personal offer for your business') }}
            v-btn(
              class="secondary-button w-100 mt-6"
              @click="planDemo"
            ) {{ $t('Plan demo') }}
            div(class="text-12px fw-300 text-center py-2") {{ $t('Discuss price') }}
            div(class="billing-plans__features")
              div(
                class="billing-plans__feature fw-500 dark"
                :class="[xlDevice ? 'text-14px' : 'text-12px']"
              )
                e-svg-icon check-black
                span {{ $t('Full options from "Premium"') }}
              div(
                class="billing-plans__feature fw-300 dark"
                :class="[xlDevice ? 'text-14px' : 'text-12px']"
              )
                e-svg-icon check-black
                span {{ $t('Individual settings for your business') }}
    div(class="billing-plans__additional-docs mt-6")
      img(
        src="~/assets/images/wink-smile.gif"
        class="billing-plans__additional-docs-icon mr-3"
      )
      span(class="text-14px dark") {{ $t('For each plan you can purchase additional outgoing documents for') }}
      div(class="billing-plans__additional-docs-price ml-2")
        span(class="billing-plans__additional-docs-price-text text-12px") {{ $t('2.40 UAH/piece') }}
    div(class="d-flex justify-center")
      v-btn(
        text
        class="mt-6"
        :disabled="loading"
        @click="toggleTable"
      )
        span {{ $t('Comparing plans table') }}
        e-svg-icon(
          size="sm"
          class="ml-2 billing-plans__toggle-icon"
          :class="{ active: showTable }"
        ) arrow-down
    transition(name="slide-fade")
      div(
        v-show="showTable"
        class="billing-plans__tables"
      )
        billing-plans-comparing-table-header(
          :plans="plans"
          :premium-plan-id="premiumPlanId"
          :free-plan-id="freePlanId"
          :check-disabled-button="checkDisabledPlan"
          :set-button-text="setButtonText"
          @planDemo="planDemo"
        )
        billing-plans-comparing-table(
          :header="$t('Documents and limits')"
          :rows="docsLimitRows"
        )
        billing-plans-comparing-table(
          :header="$t('Signing')"
          :rows="signRows"
        )
        billing-plans-comparing-table(
          :header="$t('Organization with documents')"
          :rows="organizationsRows"
        )
        billing-plans-comparing-table(
          :header="$t('Integrations')"
          :rows="integrationsRows"
        )
        billing-plans-comparing-table(
          :header="$t('Security')"
          :rows="securityRows"
        )
    div(class="billing-plans__banner")
      div(class="billing-plans__banner-left")
        img(
          src="~/assets/images/thinking-face.gif"
          class="billing-plans__banner-image"
        )
        div(class="ml-3")
          div(class="text-14px dark") {{ $t('Need help with plan selection?') }}
          div(class="text-12px") {{ $t('We will call you in 5 minutes and help select optimal variant') }}
      div(class="billing-plans__banner-right")
        v-btn(
          class="billing-plans__banner-button"
          :class="{ 'w-100': xsDevice }"
          @click="planDemo('help')"
        ) {{ $t('Need help') }}
</template>

<script>
import { mapGetters } from 'vuex'
import BillingPlansComparingTableHeader from '~/modules/billing/components/billing-plans-comparing-table-header.vue'
import BillingPlansComparingTable from '~/modules/billing/components/billing-plans-comparing-table'
import { FREE_PLAN_ID, INDIVIDUAL_PREMIUM_PLAN_ID, PREMIUM_PLAN_ID } from '~/modules/billing/constants/common'
import BillingPremiumLabel from '~/modules/billing/components/billing-premium-label'
import responsive from '~/mixins/pages/responsive'
import contentDialog from '~/mixins/dialogs/contentDialog'
import informationDialog from '~/mixins/dialogs/informationDialog'
import actions from '~/modules/billing/mixins/actions'

export default {
  name: 'BillingPlans',
  components: { BillingPlansComparingTableHeader, BillingPlansComparingTable, BillingPremiumLabel },
  mixins: [responsive, contentDialog, informationDialog, actions],
  data() {
    return {
      plans: [],
      showTable: false,
      loading: false
    }
  },
  computed: {
    ...mapGetters('billing', {
      currentPlan: 'currentPlan',
      invoice: 'invoice'
    }),
    premiumPlanId() {
      return PREMIUM_PLAN_ID
    },
    freePlanId() {
      return FREE_PLAN_ID
    },
    // todo: reuse
    isActiveFreePlan() {
      return this._.get(this.currentPlan, 'tariff.id') === FREE_PLAN_ID
    },
    isActivePremiumPlan() {
      const currentPlanId = this._.get(this.currentPlan, 'tariff.id')
      return [INDIVIDUAL_PREMIUM_PLAN_ID, PREMIUM_PLAN_ID].includes(currentPlanId)
    },
    docsLimitRows() {
      return [
        {
          title: this.$t('Incoming documents/month'),
          columns: [10, 100, this.$t('Individually')]
        },
        {
          title: this.$t('Receiving documents'),
          columns: [this.$t('Unlimited'), this.$t('Unlimited'), this.$t('Unlimited')]
        },
        {
          title: this.$t('Users in organization'),
          columns: [1, this.$t('Unlimited'), this.$t('Unlimited')]
        },
        {
          title: this.$t('Rights access users'),
          columns: ['-', 'check', 'check']
        }
      ]
    },
    signRows() {
      return [
        {
          title: this.$t('Signature via link'),
          columns: ['check', 'check', 'check']
        },
        {
          title: this.$t('Diia.Sign'),
          columns: ['check', 'check', 'check']
        },
        {
          title: this.$t('By EDS key (KEP)'),
          columns: ['check', 'check', 'check']
        },
        {
          title: 'SmartID',
          columns: ['check', 'check', 'check']
        }
      ]
    },
    organizationsRows() {
      return [
        {
          title: this.$t('Folder system'),
          columns: ['-', 'check', 'check']
        },
        {
          title: this.$t('Counterparts database'),
          columns: ['-', 'check', 'check']
        },
        {
          title: this.$t('Commenting'),
          columns: ['-', 'check', 'check']
        },
        {
          title: this.$t('Approve'),
          columns: ['-', 'check', 'check']
        },
        {
          title: this.$t('Participants route'),
          columns: ['-', 'check', 'check']
        },
        {
          title: this.$t('Canceling'),
          columns: ['-', 'check', 'check']
        }
      ]
    },
    integrationsRows() {
      return [
        {
          title: this.$t('Document widget'),
          columns: ['-', 'check', 'check']
        },
        {
          title: 'API',
          columns: ['-', 'check', 'check']
        },
        {
          title: this.$t('Custom integration'),
          columns: ['-', '-', 'check']
        }
      ]
    },
    securityRows() {
      return [
        {
          title: this.$t('Saving'),
          columns: [this.$t('Unlimited'), this.$t('Unlimited'), this.$t('Unlimited')]
        }
      ]
    }
  },
  created() {
    this.getPlans()
  },
  methods: {
    async getPlans() {
      try {
        this.loading = true
        const { data } = await this.$store.dispatch('billing/getPlans')
        this.plans = data
      } catch (e) {
        this.$handlers.error(e, this)
      } finally {
        this.loading = false
      }
    },
    setButtonText(planId) {
      if (planId === FREE_PLAN_ID) {
        if (this.isActiveFreePlan) {
          return this.$t('Active')
        }
      } else if (planId === PREMIUM_PLAN_ID) {
        if (this.isActiveFreePlan) {
          if (this.$Organization?.canActivateTrial) {
            return this.$t('Try for free')
          } else {
            return this.$t('Update to Premium')
          }
        } else if (this.isActivePremiumPlan || this.invoice) {
          return this.$t('Continue plan')
        }
      }
    },
    checkDisabledPlan(planId) {
      if (planId === FREE_PLAN_ID) {
        return this._.get(this.currentPlan, 'tariff.id', null) === FREE_PLAN_ID
      } else if (planId === PREMIUM_PLAN_ID) {
        return (this._.get(this.currentPlan, 'tariff.id', null) === PREMIUM_PLAN_ID && !this.invoice) || this._.get(this.currentPlan, 'tariff.id', null) === INDIVIDUAL_PREMIUM_PLAN_ID
      }
    },
    getPlanFeatures(description) {
      return description.split('\n')
    },
    setPlanSubtext(planId) {
      if (planId === FREE_PLAN_ID) {
        return this.$t('Free forever')
      } else if (planId === PREMIUM_PLAN_ID) {
        if (this.isActivePremiumPlan) {
          return this.$t('Subscription active until', {
            date: this.$moment
              .parseZone(this._.get(this.currentPlan, 'endDate'))
              .format('DD.MM.YYYY')
          })
        }
        return this.$t('Do not need card for activation')
      }
    },
    toggleTable() {
      this.showTable = !this.showTable
    },
    async planDemo(type) {
      const sent = await this.contentDialog.open({
        width: '512px',
        component: 'billing-individual-client-form',
        componentProps: {
          from: type === 'help' ? 'Продукт - Саппорт' : 'Продукт - Форма'
        }
      })

      if (sent) {
        await this.informationDialog.open({
          image: require('~/assets/images/check-mark.gif'),
          title: this.$t('Request sent!'),
          text: this.$t('We will contact you shortly to set up a personalized plan'),
          textClass: 'text-14px fw-300',
          width: '512px',
          buttonText: this.$t('Understand')
        })
      }
    }
  }
}
</script>

<style lang="scss">
.billing-plans {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: map-get($breakpoints, 'sm')) {
    grid-template-columns: 1fr 1fr 1fr;
  }

  &__item {
    border-radius: 16px;
    border: 1px solid $border-disabled;
    padding: 24px 16px;

    &.gradient-bg {
      background: url('assets/images/billing-premium-plan-bg.png') no-repeat;
      background-size: cover;
      border-width: 0;
    }

    &-header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      padding-bottom: 16px;
    }
  }

  &__selected-label {
    border-radius: 6px;
    background: $background-gray;
    padding: 4px 6px;
  }

  &__features {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 24px;
  }

  &__feature {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__tables {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-top: 48px;

    @media (min-width: map-get($breakpoints, 'sm')) {
      gap: 40px;
    }
  }

  &__toggle-icon {
    transition: transform linear 0.2s;

    &.active {
      transform: rotateZ(180deg);
    }
  }

  &__banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: column;
    margin-top: 24px;

    border-radius: 12px;
    background: $background-gray;
    padding: 12px 16px;

    @media (min-width: map-get($breakpoints, 'sm')) {
      flex-direction: row;
    }

    &-left {
      display: flex;
      align-items: center;
    }

    &-right {
      width: 100%;
      padding-top: 12px;

      @media (min-width: map-get($breakpoints, 'sm')) {
        width: auto;
        padding-top: 0;
      }
    }

    &-image {
      max-width: 32px;
      max-height: 32px;
      width: 100%;
      height: 100%;
    }

    &-button {
      height: 32px !important;
      border-radius: 8px;
      border: 1px solid $border-primary;
      font-size: 12px !important;
      font-weight: 400 !important;
    }
  }

  &__additional-docs {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 16px;
    background: $white;
    border-radius: 16px;
    margin-left: auto;
    margin-right: auto;
    @media (max-width: map-get($breakpoints, 'sm')) {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }
    &-icon {
      max-width: 32px;
      height: auto;
    }
    &-price {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 6px 8px;
      border-radius: 20px;
      background: $btn-secondary-text-hover;
      white-space: nowrap;
      &-text {
        color: $primary;
      }
    }
  }
}

.slide-fade-enter-active {
  transition: all 0.5s ease;
}
.slide-fade-leave-active {
  transition: all 0.5s ease;
}
.slide-fade-enter {
  transform: translateY(100%);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
